/**
 * Drop Target Utilities
 * 
 * Handles detection of valid drop targets during node drag operations.
 * Used for the nest-on-drop feature.
 */

import { getAllConditionTaskIds } from '../../../shared/ConditionsDropdown/utils/conditionHelpers';

/**
 * Get effective dependency IDs from a task
 * Checks unlockConditions first (v1 or v2), falls back to dependsOn
 */
export function getEffectiveDepIds(task, allTasks = null) {
  const ids = getAllConditionTaskIds(task.unlockConditions, allTasks);
  if (ids.length > 0) return ids;
  const deps = Array.isArray(task.dependsOn) ? task.dependsOn : [];
  return deps.map(dep => typeof dep === 'string' ? dep : dep?.taskId).filter(Boolean);
}

/**
 * Check if dragged node(s) can be dropped onto a target
 * Prevents circular dependencies and self-drops
 * @param {Object} targetTask - Potential drop target task
 * @param {Set<string>} draggedIds - Set of IDs being dragged
 * @param {Array} tasks - All tasks for dependency lookup
 * @returns {boolean} True if target is valid
 */
export function isValidDropTarget(targetTask, draggedIds, tasks) {
  // Milestones are not nest targets
  if (targetTask.nodeType === 'milestone' || targetTask.nodeType === 'note') return false;

  // Can't drop on self
  if (draggedIds.has(targetTask.id)) return false;
  
  // Can't drop on a task that depends on any dragged task
  const targetDepIds = getEffectiveDepIds(targetTask, tasks);
  for (const depId of targetDepIds) {
    if (draggedIds.has(depId)) return false;
  }
  
  // Can't drop on a task that any dragged task depends on
  for (const draggedId of draggedIds) {
    const draggedTask = tasks.find(t => t.id === draggedId);
    if (!draggedTask) continue;
    
    const draggedDepIds = getEffectiveDepIds(draggedTask, tasks);
    for (const depId of draggedDepIds) {
      if (depId === targetTask.id) return false;
    }
  }
  
  return true;
}

/**
 * Find the drop target based on CURSOR position (not dragged node position)
 * This makes the drop behavior intuitive - you drop where your cursor is, not where the node ended up
 * @param {string} draggedNodeId - ID of the node being dragged (kept for API compatibility)
 * @param {Object} cursorPosition - Current cursor position in canvas coordinates
 * @param {Set<string>} draggedIds - All IDs being dragged
 * @param {Array} tasks - All tasks
 * @param {Object} nodePositions - Auto-layout positions
 * @param {Object} customPositions - Custom positions (runtime overrides)
 * @param {Object} nodeDimensions - Node dimensions
 * @returns {Object|null} The target task object or null
 */
export function findDropTarget(
  draggedNodeId, // kept for API compatibility
  cursorPosition,
  draggedIds,
  tasks,
  nodePositions,
  customPositions,
  nodeDimensions
) {
  // Find which node the cursor is over
  const draggedIncludesMilestone = [...draggedIds].some((id) => {
    const t = tasks.find((task) => task.id === id);
    return t?.nodeType === 'milestone' || t?.nodeType === 'note';
  });
  if (draggedIncludesMilestone) return null;

  for (const task of tasks) {
    if (!isValidDropTarget(task, draggedIds, tasks)) continue;

    // Get position: customPositions > task.x/y > nodePositions
    let targetPos = customPositions[task.id];
    if (!targetPos && typeof task.x === 'number' && typeof task.y === 'number') {
      targetPos = { x: task.x, y: task.y };
    }
    if (!targetPos) {
      targetPos = nodePositions[task.id];
    }
    if (!targetPos) continue;

    // Get dimensions with sensible defaults
    const targetDims = nodeDimensions[task.id] || { width: 290, height: 150 };
    
    // Check if cursor is inside this node's bounding box
    const isInside = 
      cursorPosition.x >= targetPos.x &&
      cursorPosition.x <= targetPos.x + targetDims.width &&
      cursorPosition.y >= targetPos.y &&
      cursorPosition.y <= targetPos.y + targetDims.height;
    
    if (isInside) {
      return task;
    }
  }

  return null;
}
